import { useInfiniteQuery } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import useHttpClient from './useHttpClient';

export type TeamTypes = 'Follower' | 'Collaborator' | 'Guest';

export interface ITeamMember {
  userId: {
    native: number;
  };
  username: string;
  email: string;
  fullname: string;
  isPrimary: boolean;
  isAdmin: boolean;
  level: TeamTypes;
  pictureUrl: string;
  isFirstPrimary: boolean;
  isOnlyPrimary: boolean;
}

interface IProjectTeam {
  count: number;
  offset: number;
  limit: number;
  hasMore: boolean;
  requestedFields: string;
  items: ITeamMember[];
}

interface IProjectTeamPayload {
  loading: boolean;
  error?: Error;
  team?: IProjectTeam;
  called: boolean;
}

export interface IQueryProjectTeamHook {
  projectTeamSearchResults: IProjectTeamPayload;
  getProjectTeam: (projectId: number, searchTerm: string, limit?: number) => void;
  getMoreProjectTeam: (projectId: number, searchTerm: string, limit?: number) => void;
}

const useQueryProjectTeam = (): IQueryProjectTeamHook => {
  const httpClient = useHttpClient();
  const [searchParams, setSearchParams] = useState<{
    projectId: number;
    searchTerm: string;
    limit: number;
  } | null>(null);

  const { data, error, isLoading, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ['projectTeam', searchParams?.projectId, searchParams?.searchTerm, searchParams?.limit],
    queryFn: async ({ pageParam = 0 }) => {
      if (!searchParams) {
        throw new Error('Search parameters not set');
      }
      const { projectId, searchTerm, limit } = searchParams;
      const params = new URLSearchParams({
        limit: limit.toString(),
        searchTerm,
        offset: pageParam.toString(),
      });
      const response = await httpClient.get(`/projects/${projectId}/team?${params.toString()}`);
      return response.data as IProjectTeam;
    },
    getNextPageParam: (lastPage: IProjectTeam, allPages) => {
      return lastPage.hasMore ? allPages.reduce((total, page) => total + page.items.length, 0) : undefined;
    },
    enabled: searchParams !== null,
  });

  const getProjectTeam = useCallback((projectId: number, searchTerm: string, limit: number = 50) => {
    setSearchParams({ projectId, searchTerm, limit });
  }, []);

  const getMoreProjectTeam = () => {
    if (hasNextPage && !isLoading) {
      fetchNextPage();
    }
  };

  // Flatten all pages into a single team object
  const allItems = data?.pages.flatMap(page => page.items) || [];
  const lastPage = data?.pages[data.pages.length - 1];

  const team: IProjectTeam | undefined = lastPage ? {
    count: lastPage.count,
    offset: 0,
    limit: lastPage.limit,
    hasMore: hasNextPage || false,
    requestedFields: lastPage.requestedFields,
    items: allItems,
  } : undefined;

  return {
    projectTeamSearchResults: {
      error: error as Error,
      loading: isLoading,
      team,
      called: searchParams !== null,
    },
    getProjectTeam,
    getMoreProjectTeam,
  };
};

export default useQueryProjectTeam;
